import { Builder } from '@builder.io/sdk';
import { S15TwoSectionsSection } from '../components/interactive-sections/S15TwoSections';
import { BUILDER_INTERACTIVE_SECTION_COMPONENTS_NAMES } from '../BuilderComponentsName';
import {
    altBackgroundColorOptions,
    backgroundColorOptions,
    backgroundGradientOptions,
    textColorOptionsLimited,
    waveColorOptions,
} from '../utils/builderColorOptions';
import { getBackgroundGradientInput, getPaddingInputs, getBackgroundColorInput, getWaveGradientInput } from '../utils/builderSharedInputs';

Builder.registerComponent(S15TwoSectionsSection, {
    name: BUILDER_INTERACTIVE_SECTION_COMPONENTS_NAMES.S15TwoSections,
    friendlyName: 'S15 Two Sections',
    inputs: [
        {
            name: 'headlineText',
            type: 'string',
            friendlyName: 'Headline',
            required: true,
            defaultValue: 'Lifetime Guarantee - THE UNLIMITED PACKAGE™',
            helperText: 'The main heading displayed at the top of the section'
        },
        {
            name: 'headlineColor',
            type: 'string',
            friendlyName: 'Headline Color',
            enum: textColorOptionsLimited,
            defaultValue: 'Blue',
            helperText: 'Select a color for the headline text'
        },
        {
            name: 'text',
            type: 'string',
            friendlyName: 'Text',
            required: true,
            defaultValue: 'We offer the industry\'s first and only Unlimited Package™ with all the treatments you need at no extra cost and FREE touch-ups for life!',
            helperText: 'Supporting text content below the headline. Available variables: {{clinicCount}}, {{totalReviews}}, {{stateCount}}, {{treatmentsPerMonth}}'
        },
        {
            name: 'textColor',
            type: 'string',
            friendlyName: 'Text Color',
            enum: textColorOptionsLimited,
            defaultValue: 'Navy',
            helperText: 'Select a color for the main text content'
        },
        {
            name: 'itemBackgroundColor',
            type: 'string',
            friendlyName: 'Item Background Color',
            enum: backgroundColorOptions,
            defaultValue: 'White',
            helperText: 'Select the background color for individual items'
        },
        {
            name: 'itemHeadlineColor',
            type: 'string',
            friendlyName: 'Item Headline Color',
            enum: textColorOptionsLimited,
            defaultValue: 'Turquoise',
            helperText: 'Select the color for item headlines'
        },
        {
            name: 'itemTextColor',
            type: 'string',
            friendlyName: 'Item Text Color',
            enum: textColorOptionsLimited,
            defaultValue: 'Navy',
            helperText: 'Select the color for item text content'
        },
        {
            name: 'items',
            friendlyName: 'Content Items',
            type: 'list',
            required: true,
            onChange: (options) => {
                // Check if the list has more than 4 items
                if (options.get('items').length > 4) {
                    options.set('items', options.get('items').slice(0, 4))
                    alert('maximum items are 4, delete items to continue')
                }
                // check if the list has less than 2 items
                if (options.get('items').length < 2) {
                    alert('minimum items are 2, add items to continue')
                }
            },
            defaultValue: [
                {
                    headlineText: 'One Price',
                    imageAlt: 'First section image',
                    imagePosition: false,
                    imageSrc: 'https://cdn.builder.io/api/v1/image/assets%2F10266dac478d4c768f6b6fbe586ae30e%2Fb64bfa298643445f9312bb790d05b836',
                    text: 'Most laser hair removal providers charge you per session, so you’re stuck with unpredictable costs. We offer the industry’s first and only Unlimited Package™ with all the treatments you need for one price plus FREE touch-ups for life—no hidden fees ever.'
                },
                {
                    headlineText: 'Smooth For Life',
                    imageAlt: 'Second section image',
                    imagePosition: true,
                    imageSrc: 'https://cdn.builder.io/api/v1/image/assets%2F10266dac478d4c768f6b6fbe586ae30e%2Fce5ec622584f4597bee20150192effed',
                    text: 'Clients never have to worry about surprise hair growth. Whether it’s due to hormonal changes or dormant follicles becoming active, you’ll always get the treatments and touch-ups you need at no extra cost. Think of it as your insurance policy for forever-smooth skin!'
                }
            ],
            helperText: 'Add content items with image and text',
            subFields: [
                {
                    name: 'headlineText',
                    type: 'string',
                    friendlyName: 'Headline Text',
                    required: true,
                    helperText: 'Enter the headline for this item. Available variables: {{clinicCount}}, {{totalReviews}}, {{stateCount}}, {{treatmentsPerMonth}}'
                },
                {
                    name: 'imageAlt',
                    type: 'string',
                    friendlyName: 'Image Alt Text',
                    required: true,
                    helperText: 'Alt text for the image'
                },
                {
                    name: 'imagePosition',
                    type: 'boolean',
                    friendlyName: 'Image Position',
                    defaultValue: false,
                    required: true,
                    helperText: 'Toggle image position from left to right'
                },
                {
                    name: 'imageSrc',
                    type: 'file',
                    friendlyName: 'Image',
                    required: true,
                    allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg', 'webp'],
                    helperText: 'Upload the image for this item'
                },
                {
                    name: 'text',
                    type: 'string',
                    friendlyName: 'Text Content',
                    required: true,
                    helperText: 'Enter the text content for this item. Available variables: {{clinicCount}}, {{totalReviews}}, {{stateCount}}, {{treatmentsPerMonth}}'
                }
            ]
        },
        {
            name: 'hasBackgroundWave',
            type: 'boolean',
            friendlyName: 'Show Background Wave',
            defaultValue: true,
            helperText: 'Toggle to show or hide the background wave. This will not work if background gradient is selected.'
        },
        {
            name: 'backgroundFlipWave',
            type: 'boolean',
            friendlyName: 'Flip Background Wave',
            defaultValue: false,
            helperText: 'Toggle to flip the background wave direction'
        },
        {
            name: 'backgroundWaveColor',
            type: 'string',
            friendlyName: 'Background Wave Color',
            enum: waveColorOptions,
            defaultValue: 'White',
            helperText: 'Select the color for the background wave'
        },
        ...getWaveGradientInput({
            defaultValue: 'Blue10Top',
            waveGradientList: backgroundGradientOptions,
            helperText: 'Gradient for wave effect. Requires wave to be enabled. Set Background Color and Background Gradient to None if using this.'
        }),
        ...getBackgroundColorInput({
            backgroundColorsList: altBackgroundColorOptions,
            helperText: 'Solid background color. Set Background Gradient to None if using this.'
        }),
        ...getBackgroundGradientInput({
            backgroundGradientList: backgroundGradientOptions,
            helperText: 'Background gradient without wave. Set Background Color to None if using this.'
        }),
        {
            name: 'backgroundImage',
            type: 'file',
            allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg', 'webp'],
            friendlyName: 'Background Image',
            helperText: 'Upload an image to use as the section background'
        },
        ...getPaddingInputs({
            desktopTop: "large",
            desktopBottom: "large",
            mobileTop: "small",
            mobileBottom: "small"
        }),
    ]
});
